import { useEffect, type RefObject } from "react";

// A single shared window listener feeds all tracked elements, rather than one listener per hook instance.
const mouse = { x: 0, y: 0 };
const tracked = new Map<
  HTMLElement,
  { maxOffset: number; x: number; y: number }
>();
const visible = new Set<HTMLElement>();
let io: IntersectionObserver | null = null;
let dirty = false;
let raf = 0;
let lastFrame = 0;

function markDirty() {
  dirty = true;
}

function onMouseMove(e: MouseEvent) {
  mouse.x = e.clientX;
  mouse.y = e.clientY;
  dirty = true;
}

function frame(now: number) {
  raf = requestAnimationFrame(frame);
  const dt = now - lastFrame;
  lastFrame = now;
  if (!dirty) return;
  const ease = 1 - Math.pow(0.92, dt / (1000 / 60));
  const modalOpen = document.querySelector("dialog:modal") !== null;
  const writes: Array<[HTMLElement, string]> = [];
  let settled = true;
  for (const [el, state] of tracked) {
    if (!visible.has(el)) continue;
    let tx = 0;
    let ty = 0;
    if (!modalOpen || el.closest("dialog:modal") !== null) {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = mouse.x - cx;
      const dy = mouse.y - cy;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const strength = Math.min(dist, 200) / 200;
      const k = dist > 0 ? (strength * state.maxOffset) / dist : 0;
      tx = dx * k;
      ty = dy * k;
    }
    state.x += (tx - state.x) * ease;
    state.y += (ty - state.y) * ease;
    if (Math.abs(tx - state.x) > 0.05 || Math.abs(ty - state.y) > 0.05) {
      settled = false;
    } else {
      state.x = tx;
      state.y = ty;
    }
    writes.push([el, `translate(${state.x}px, ${state.y}px)`]);
  }
  for (const [el, transform] of writes) {
    el.style.transform = transform;
  }
  if (settled) dirty = false;
}

function start() {
  io = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        visible.add(entry.target as HTMLElement);
      } else {
        visible.delete(entry.target as HTMLElement);
      }
    }
    dirty = true;
  });
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("scroll", markDirty, true);
  window.addEventListener("resize", markDirty);
  lastFrame = performance.now();
  raf = requestAnimationFrame(frame);
}

function stop() {
  cancelAnimationFrame(raf);
  io?.disconnect();
  io = null;
  visible.clear();
  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("scroll", markDirty, true);
  window.removeEventListener("resize", markDirty);
}

export function useMouseTracking<T extends HTMLElement>(
  ref: RefObject<T | null>,
  maxOffset: number
) {
  useEffect(() => {
    // 0 disables tracking, freeing the element's transform for a CSS animation
    // (e.g. the sleeping avatar's breathing) to own.
    if (maxOffset === 0) return;
    const el = ref.current;
    if (!el) return;
    if (tracked.size === 0) start();
    tracked.set(el, { maxOffset, x: 0, y: 0 });
    io?.observe(el);
    return () => {
      tracked.delete(el);
      visible.delete(el);
      io?.unobserve(el);
      if (tracked.size === 0) stop();
    };
  }, [ref, maxOffset]);
}
